import { getBodyShadowSheets } from '../helper/bodyShadowSheet'

/**
 * 본문 mount 방식(host 가 ViewerIFace Settings.bodyIsolation 으로 지정).
 * - 'light-dom'(기본, 값이 없을 때 포함): 본문을 `.htmlMailBody` div 에 바로 넣는다(기존 경로).
 * - 'shadow-dom': `.htmlMailBody` host 의 open shadow root 안 wrapper 에 넣는다 — document·viewer
 *   stylesheet 가 본문 요소에 매칭되지 않아, 대형 본문의 첫 style recalc 를 크게 줄인다.
 * 값 이름은 theme('light'/'dark')과 섞이지 않도록 '-dom' 접미사로 명확히 한다.
 */
export type BodyIsolation = 'light-dom' | 'shadow-dom'

let warned = false

/** shadow 격리에 필요한 API 가 모두 있는지. */
export function canIsolateBody(): boolean {
  return (
    typeof Element !== 'undefined' &&
    typeof Element.prototype.attachShadow === 'function' &&
    typeof CSSStyleSheet !== 'undefined' &&
    typeof CSSStyleSheet.prototype.replaceSync === 'function' &&
    typeof ShadowRoot !== 'undefined' &&
    'adoptedStyleSheets' in ShadowRoot.prototype
  )
}

/**
 * 본문 DOM 을 넣을 요소를 돌려준다. light-dom 이면 ref 그대로, shadow-dom 이면 ref 의 shadow root 안 wrapper.
 * wrapper 는 ref 와 같은 class(hash + literal `htmlMailBody`)를 가져, `closest('.htmlMailBody')` 판정이
 * shadow 안에서도 그대로 성립한다. API 가 없으면 light-dom 으로 떨어지고 경고는 1회만 남긴다.
 */
export function mountBodyRoot(ref: HTMLElement, isolation: BodyIsolation | undefined): HTMLElement {
  // 한 번 mount 한 방식은 그 ref 에서 바꾸지 않는다 — 설정이 mount 뒤 바뀌어도(재bake) 본문이 shadowRoot 에
  // 가려지거나 light 에 남은 채 둘로 갈리지 않게. 새 방식은 새 ref(viewer remount)부터 적용된다.
  if (ref.shadowRoot == null && (isolation !== 'shadow-dom' || ref.hasChildNodes())) return ref
  if (!canIsolateBody()) {
    if (!warned) {
      warned = true
      console.warn('[viewer] bodyIsolation=shadow-dom 미지원 환경 — light-dom 으로 mount 한다')
    }
    return ref
  }
  const root = ref.shadowRoot ?? ref.attachShadow({ mode: 'open' })
  root.adoptedStyleSheets = getBodyShadowSheets()
  let wrapper = shadowWrapper(root)
  if (wrapper == null) {
    wrapper = document.createElement('div')
    root.replaceChildren(wrapper)
  }
  wrapper.className = ref.className
  return wrapper
}

function shadowWrapper(root: ShadowRoot): HTMLElement | null {
  const first = root.firstElementChild
  return first instanceof HTMLElement && first.classList.contains('htmlMailBody') ? first : null
}

/**
 * 본문 event 의 실제 대상 — shadow 안 event 는 바깥 listener 에서 e.target 이 host 로 retarget 된다.
 * composedPath()[0] 이 실제 요소다(light 에서는 e.target 과 같다).
 */
export const bodyEventTarget = (e: Event): HTMLElement =>
  (e.composedPath?.()[0] ?? e.target) as HTMLElement

/** 본문 DOM 의 실제 부모 — shadow mount 면 wrapper, 아니면 ref. 본문을 읽는 곳(print·remount)이 거친다. */
export function bodyContentRoot(ref: HTMLElement): HTMLElement {
  // divFrameMap 은 ref 콜백이 아직 안 돈 시점에 null 을 담을 수 있다(OMD-240 복원 경합). 이때는 ref 를
  // 그대로 돌려줘, 소비처(applyBlockedImg·applyInlineImg)의 null 가드가 안전하게 건너뛰게 한다.
  if (ref == null) return ref
  return (ref.shadowRoot != null ? shadowWrapper(ref.shadowRoot) : null) ?? ref
}
